import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Button, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid-premium';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import ViewTable from '../page-layouts/view/table/ViewTable';
import { renderCellWithTooltip, renderDate } from '../page-layouts/view/table/ViewTableUtils';

import useSettings from '../hooks/useSettings';
import { parseDate } from '../Utils';
import Text from '../Text';

export interface FieldLogsDialogProps {
	/** The record module */
	module: string;
	/** The record uid */
	uid?: string;
	/** The logged field name */
	fieldName: string;
	/** The logged field label */
	fieldLabel?: React.ReactNode;
	/** Function used to parse the logs values */
	fieldParser?: (fieldValue: any) => React.ReactNode;
	/** The updateRefresh function, used to refresh the views if something changes */
	updateRefresh: () => void;
}

export const showFieldLogsDialog = (props: FieldLogsDialogProps) =>
	openDialog({
		children: <FieldLogsDialog {...props} />,
		classes: { paper: 'h-full' },
		maxWidth: 'lg',
		fullWidth: true
	});

/**
 * This dialog component allows the user to see the logs of a field
 */
export default function FieldLogsDialog({
	module,
	uid,
	fieldName,
	fieldLabel,
	fieldParser,
	updateRefresh
}: FieldLogsDialogProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();

	function parseValue(value) {
		if (!value)
			return (
				<Text className="font-400 italic" variant="subtitle2" color="text.disabled">
					NULL
				</Text>
			);

		if (fieldParser) return fieldParser(value);

		return renderCellWithTooltip({ value });
	}

	const columns: GridColDef[] = [
		{
			field: 'user_title',
			headerName: t('Utente'),
			minWidth: 200,
			flex: 2,
			renderCell: ({ value, row }) =>
				renderCellWithTooltip({ value, to: { module: 'users', uid: row.logs_user } }),
			sortable: false
		},
		{
			field: 'logs_date',
			headerName: t('Data'),
			minWidth: 100,
			flex: 1,
			renderCell: ({ value }) =>
				renderDate({
					value,
					format: {
						year: 'numeric',
						month: '2-digit',
						day: '2-digit',
						hour: '2-digit',
						minute: '2-digit'
					}
				}),
			sortable: false
		},
		{
			field: 'logs_oldtitle',
			headerName: t('Vecchio valore'),
			minWidth: 200,
			flex: 2,
			renderCell: ({ value, row }) => parseValue(value || row.logs_oldvalue),
			sortable: false,
			hideable: false
		},
		{
			field: 'logs_newtitle',
			headerName: t('Nuovo valore'),
			minWidth: 200,
			flex: 2,
			renderCell: ({ value, row }) => parseValue(value || row.logs_newvalue),
			sortable: false,
			hideable: false
		}
		// {
		// 	field: 'logs_oldtitle',
		// 	headerName: t('Valore'),
		// 	minWidth: 200,
		// 	flex: 4,
		// 	renderCell: ({ value, row }) => parseValue(value || row.logs_oldvalue),
		// 	sortable: false
		// }
	];

	return (
		<>
			<DialogTitle>
				<FormattedMessage
					id="fieldLogsDialog.title"
					defaultMessage={t('DIALOG_TITLE_SHOW_FIELD_LOG')}
					values={{ field: fieldLabel }}
				/>
			</DialogTitle>
			<DialogContent>
				<ViewTable
					className="h-full"
					module="sys_logs_updates"
					columns={columns}
					defaultFilters={[
						// DA TENERE COMMENTATA, ALTRIMENTI NON SI VEDONO I LOG DALLE TABELLE EXTENSIONS
						// { filt_field: 'logs_table', filt_clause: 'equals', filt_value: module },
						{ filt_field: 'logs_table_uid', filt_clause: 'equals', filt_value: uid },
						{ filt_field: 'logs_field', filt_clause: 'equals', filt_value: fieldName }
					]}
					showFilters={false}
					showExportButton={false}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => dispatch(closeDialog())}>{t('DIALOG_ACTION_CLOSE')}</Button>
			</DialogActions>
		</>
	);
}
